import type { LedgerPostingId, PaymentId } from '../../BrandedIds';
import type { GLAccount } from './GLAccount';
import { type AccountAttributionBalance, type AccountBalances, LedgerIndex } from './LedgerIndex';
import type { Posting } from './Posting';
import type { Transaction } from './Transaction';

// Once-per-call queries against a ledger by constructing a fresh
// `LedgerIndex`. Long-lived callers should use `LoanLedger.index`
// directly — it's incrementally maintained.

export { type AccountAttributionBalance, type AccountBalances, type AttributionFields } from './LedgerIndex';

export const deriveBalances = (transactions: ReadonlyArray<Transaction>): AccountBalances =>
  new LedgerIndex(transactions).snapshot();

// Includes the transaction with the matching id. Returns all
// transactions if no match (matches `deriveBalances`).
export const deriveBalancesUpTo = (
  transactions: ReadonlyArray<Transaction>,
  untilTransactionId: Transaction['id'],
): AccountBalances => {
  const stopIdx = transactions.findIndex((t) => t.id === untilTransactionId);
  const prefix = stopIdx === -1 ? transactions : transactions.slice(0, stopIdx + 1);
  return new LedgerIndex(prefix).snapshot();
};

export type ReversalIndex = {
  reversalByOriginalId: Map<LedgerPostingId, Posting>;
  originalByReversalId: Map<LedgerPostingId, Posting>;
};

export const indexReversals = (transactions: ReadonlyArray<Transaction>): ReversalIndex => {
  const postingsById = new Map<LedgerPostingId, Posting>();
  for (const txn of transactions) {
    for (const posting of txn.postings) {
      postingsById.set((posting as unknown as { id: LedgerPostingId }).id, posting);
    }
  }

  const reversalByOriginalId = new Map<LedgerPostingId, Posting>();
  const originalByReversalId = new Map<LedgerPostingId, Posting>();

  for (const txn of transactions) {
    for (const posting of txn.postings) {
      const concrete = posting as unknown as { id: LedgerPostingId; voidsPostingId?: LedgerPostingId };
      if (!concrete.voidsPostingId) continue;
      const original = postingsById.get(concrete.voidsPostingId);
      if (!original) continue;
      reversalByOriginalId.set((original as unknown as { id: LedgerPostingId }).id, posting);
      originalByReversalId.set(concrete.id, original);
    }
  }

  return { reversalByOriginalId, originalByReversalId };
};

// FIFO list of attribution buckets with positive balance for `account`.
// Drain policy lives in `LedgerIndex.applyFifo` (matched-first then FIFO).
export const deriveAccountAttributionBalances = (
  transactions: ReadonlyArray<Transaction>,
  account: GLAccount,
): AccountAttributionBalance[] => new LedgerIndex(transactions).fifo(account);

export type SuspenseSource = {
  paymentId: PaymentId;
  remaining: number;
};

export const deriveSuspenseSources = (transactions: ReadonlyArray<Transaction>): SuspenseSource[] => [
  ...new LedgerIndex(transactions).suspenseSources(),
];
